import { useState } from 'react';
import Modal from '@mui/material/Modal';
import Dialog from '@mui/material/Dialog';
import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import PrizeForm from './PrizeForm.jsx';
import PrizePreview from './PrizePreview.jsx';
import usePrizeForm from './usePrizeForm';
import Text from '../../../../shared/Text';
import { ButtonBase } from '../../../../shared/Button';
import ModalConfirmation from '../../../../shared/ModalConfirmation/ModalConfirmation.jsx';
import { useCampaignContext } from '../../contexts/CampaignContext.jsx';

const PrizeModal = ({ prize, modalOpen, closeModal }) => {
  const { t } = useTranslation('campaigns');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const { handleDestroyPrize, values, ...form } = usePrizeForm({
    prizeValues: prize,
  });
  const { campaign } = useCampaignContext();
  const isDisabled = campaign.isArchived || !!campaign.deletedAt;

  const handleSkipPrizes = () => {
    if (values.id) {
      setOpenDeleteModal(true);
    } else {
      closeModal();
    }
  };

  return (
    <ModalContainer open={modalOpen} onClose={closeModal}>
      <Wrapper>
        <Dialog
          open={openDeleteModal}
          onClose={() => setOpenDeleteModal(false)}
        >
          <DialogContainer>
            <ModalConfirmation
              variant="error"
              onSave={() => {
                handleDestroyPrize();
                setOpenDeleteModal(false);
                closeModal();
              }}
              onCancel={() => setOpenDeleteModal(false)}
              header={t('prizes.deleteModal.title')}
              body={t('prizes.deleteModal.body')}
              cancelText={t('prizes.deleteModal.cancel')}
              submitText={t('prizes.deleteModal.delete')}
            />
          </DialogContainer>
        </Dialog>

        <Nav>
          <NavLeft>
            <Header variant="header">{t('prizes.shortName')}</Header>
          </NavLeft>
          <NavRight>
            <Cancel
              onClick={closeModal}
              mode="outline"
              variant="black"
              disabled={isDisabled}
            >
              {t('prizes.cancel')}
            </Cancel>
            <SkipPrizes
              onClick={handleSkipPrizes}
              mode="outline"
              variant="black"
              disabled={isDisabled}
            >
              <Ban /> {t('prizes.skipPrizes')}
            </SkipPrizes>
            <SaveBtn
              mode="contained"
              variant="success"
              type="submit"
              disabled={isDisabled}
              onClick={() => {
                form.handleSubmit();
                closeModal();
              }}
            >
              <CheckMark />
              {t('prizes.savePrizes')}
            </SaveBtn>
          </NavRight>
        </Nav>
        <Container>
          <ContentWrapper>
            <Text as="div" variant="subtitle1" mb={'50px'}>
              {t('prizes.header')}
            </Text>
            <FormWrapper>
              <PrizeFormWrapper>
                <PrizeForm isShow={false} values={values} {...form} />
              </PrizeFormWrapper>
              <PrizePreview prize={values} ticketsCount={0} />
            </FormWrapper>
          </ContentWrapper>
        </Container>
      </Wrapper>
    </ModalContainer>
  );
};

export default PrizeModal;

const Wrapper = styled.div`
  background-color: ${themeGet('colors.white')};
  border-radius: 8px;
  max-width: 1025px;
  margin: 50px auto;
`;

const ModalContainer = styled(Modal)`
  overflow: scroll;
`;

const DialogContainer = styled.div`
  padding: 24px;
`;

const Nav = styled.nav`
  display: flex;
  height: 90px;
  justify-content: space-between;
  padding: 21px 20px 19px 20px;
  border-bottom: 1px solid ${themeGet('colors.black.10')};
`;

const Header = styled.div`
  line-height: 24px;
  cursor: pointer;
  color: ${themeGet('colors.black.80')};
  font-size: 18px;
  font-weight: 500;
  font-family: 'Montserrat', sans-serif;
  padding: 12px 10px 15px;
  margin: 0 9px 0 0;

  &:last-child {
    margin: 0;
  }
`;

const NavLeft = styled.div`
  display: flex;
`;
const NavRight = styled(NavLeft)``;

const Cancel = styled(ButtonBase)`
  margin-right: 15px;
  padding: 12px 29.3px;
  font-size: 16px;
  font-weight: 500;
`;

const SkipPrizes = styled(Cancel)``;

const SaveBtn = styled(ButtonBase)`
  padding: 12px 20px;
  font-size: 16px;
  font-weight: 500;
`;

const Ban = styled.i.attrs({
  className: 'fa-regular fa-ban fa-lg',
})`
  margin-right: 5px;
`;

const CheckMark = styled.i.attrs({
  className: 'fa-regular fa-check fa-lg',
})`
  margin-right: 5px;
`;

const Container = styled.div`
  background-color: ${themeGet('colors.background')};
  padding: 19px 20px;
  border-radius: 8px;
  height: 100%;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${themeGet('colors.white')};
  min-height: 496px;
  border: 1px solid ${themeGet('colors.black.10')};
  border-radius: 8px;
  padding: 28px 40px 28px 21px;
`;

const FormWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const PrizeFormWrapper = styled.div`
  width: 50%;
`;
